import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import { formatPhone } from '@monrdv/shared';
import { request } from '../lib/api.js';

/**
 * Judging two transcripts of the same recording.
 *
 * We hold two accounts of what was said on 766 voicemails, they disagree, and no arithmetic can
 * settle it — word error rate between two transcripts measures difference, not correctness.
 * Only a person who listens can say which is better, so this screen exists to make listening
 * and deciding as fast as possible.
 *
 * That goal drives every choice here:
 *
 * **Keyboard first.** 766 calls is an afternoon's work with a mouse and twenty minutes with
 * four keys. Space plays, 1–4 record a verdict and advance, J and K move. The buttons exist for
 * discoverability and are not the intended path.
 *
 * **The difference is shown, not left to be found.** Two paragraphs of similar French are
 * exactly the thing an eye slides over. Words unique to one side are marked, so the question
 * becomes "is *this* word right" rather than "read both and form an impression".
 *
 * **The audio comes first in the layout**, because a judgement made without listening is worth
 * nothing and a screen that puts the text first invites exactly that.
 *
 * The one thing deliberately absent is a winner. The tally at the top reports the checkable
 * fact — whether each transcript contains the number the switch recorded — and the verdicts
 * people have entered. It never combines them into a score, because the two measure different
 * things and averaging them would produce a number that sounds authoritative and means nothing.
 */

type Row = {
  id: string;
  startedAt: string;
  durationSeconds: number;
  outcome: string;
  fromNumber: string;
  theirs: { length: number; hasNumber: boolean | null; language: string | null };
  ours: { length: number; hasNumber: boolean | null; language: string | null; confidence: number | null };
  verdict: string | null;
  reviews: number;
};

type Score = {
  comparable: number;
  reviewed: number;
  numberCheckable: number;
  theirsNumberHits: number;
  oursNumberHits: number;
  verdicts: Record<string, number>;
};

type List = { items: Row[]; total: number; page: number; pageSize: number; pageCount: number; score: Score };

type Detail = {
  id: string;
  startedAt: string;
  durationSeconds: number;
  fromNumber: string;
  knownNumber: string | null;
  recordingUrl: string | null;
  theirs: { text: string | null; summary: string | null; language: string | null; hasNumber: boolean | null };
  ours: {
    text: string | null;
    language: string | null;
    confidence: number | null;
    model: string | null;
    hasNumber: boolean | null;
  };
  reviews: Array<{ verdict: string; note: string | null; at: string; reviewer: string | null; mine: boolean }>;
};

const VERDICTS = ['theirs', 'ours', 'equal', 'both_poor'] as const;

export function TranscriptsScreen() {
  const { t } = useTranslation();
  const search = useSearch({ from: '/transcripts' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const state = search.state ?? 'pending';
  const [cursor, setCursor] = useState(0);

  const setSearch = (patch: Record<string, unknown>) =>
    navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }), replace: true });

  const list = useQuery({
    queryKey: ['transcripts', { state, disagreeing: search.disagreeing }],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ state, limit: '100' });
      if (search.disagreeing) params.set('disagreeing', 'true');
      return request<List>(`/transcripts?${params}`, { signal });
    },
    placeholderData: (prev) => prev,
  });

  const items = list.data?.items ?? [];
  const current = items[Math.min(cursor, Math.max(items.length - 1, 0))];

  const detail = useQuery({
    queryKey: ['transcripts', 'detail', current?.id],
    queryFn: ({ signal }) => request<Detail>(`/transcripts/${current!.id}`, { signal }),
    enabled: Boolean(current),
  });

  const judge = useMutation({
    mutationFn: ({ id, verdict }: { id: string; verdict: string }) =>
      request<void>(`/transcripts/${id}/verdict`, { method: 'POST', body: JSON.stringify({ verdict }) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['transcripts'] });
      // Straight to the next one: the whole point is not to touch the mouse between calls.
      setCursor((c) => c + 1);
    },
  });

  /*
   * The keyboard. Bound on the window rather than a focused element, because the reviewer's
   * attention is on the audio and nothing here should require clicking into a region first.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) return;

      if (event.key === ' ') {
        event.preventDefault();
        const audio = audioRef.current;
        if (audio) void (audio.paused ? audio.play() : audio.pause());
        return;
      }
      if (event.key === 'j' || event.key === 'ArrowDown') return setCursor((c) => Math.min(c + 1, items.length - 1));
      if (event.key === 'k' || event.key === 'ArrowUp') return setCursor((c) => Math.max(c - 1, 0));

      const index = ['1', '2', '3', '4'].indexOf(event.key);
      if (index >= 0 && current) judge.mutate({ id: current.id, verdict: VERDICTS[index]! });
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items.length, current, judge]);

  const diff = useMemo(
    () => wordDiff(detail.data?.theirs.text ?? '', detail.data?.ours.text ?? ''),
    [detail.data?.theirs.text, detail.data?.ours.text],
  );

  const score = list.data?.score;

  return (
    <div className="screen transcripts">
      <header className="transcripts__head">
        <div>
          <h1 className="transcripts__title">{t('transcripts.title')}</h1>
          <p className="transcripts__lede">{t('transcripts.lede')}</p>
        </div>

        {/*
          Two numbers, never combined. The left is checkable without a human; the right is what
          humans concluded. A single "score" mixing them would be a number that sounds like a
          measurement and is not one.
        */}
        {score && (
          <div className="scoreboard">
            <div className="scoreboard__block">
              <span className="scoreboard__label">{t('transcripts.numberRecovered')}</span>
              <span className="scoreboard__pair tnum">
                <span title={t('transcripts.theirs')}>
                  {score.theirsNumberHits}
                </span>
                <span className="scoreboard__vs">/</span>
                <span title={t('transcripts.ours')} className="scoreboard__ours">
                  {score.oursNumberHits}
                </span>
              </span>
              <span className="scoreboard__of">{t('transcripts.outOf', { count: score.numberCheckable })}</span>
            </div>

            <div className="scoreboard__block">
              <span className="scoreboard__label">{t('transcripts.judged')}</span>
              <span className="scoreboard__pair tnum">
                {score.reviewed}
                <span className="scoreboard__vs">/</span>
                {score.comparable}
              </span>
              <span className="scoreboard__of">
                {VERDICTS.filter((v) => score.verdicts[v])
                  .map((v) => `${t(`transcripts.verdict.${v}`)} ${score.verdicts[v]}`)
                  .join(' · ') || t('transcripts.noneYet')}
              </span>
            </div>
          </div>
        )}
      </header>

      <div className="transcripts__filters">
        <div className="segmented" role="tablist">
          {(['pending', 'reviewed', 'all'] as const).map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={state === option}
              className={`segmented__option${state === option ? ' segmented__option--on' : ''}`}
              onClick={() => {
                setSearch({ state: option === 'pending' ? undefined : option });
                setCursor(0);
              }}
            >
              {t(`transcripts.state.${option}`)}
            </button>
          ))}
        </div>

        <label className="transcripts__toggle">
          <input
            type="checkbox"
            checked={Boolean(search.disagreeing)}
            onChange={(e) => {
              setSearch({ disagreeing: e.target.checked || undefined });
              setCursor(0);
            }}
          />
          {t('transcripts.onlyDisagreeing')}
        </label>

        <span className="transcripts__hint">{t('transcripts.keys')}</span>
      </div>

      {list.isPending && <p className="screen__loading">{t('common.loading')}</p>}
      {list.data && items.length === 0 && (
        <div className="empty">
          <span className="empty__title">{t('transcripts.empty.title')}</span>
          <p className="empty__body">{t('transcripts.empty.body')}</p>
        </div>
      )}

      {current && detail.data && (
        <div className="judge">
          <header className="judge__head">
            <span className="tnum">
              {formatPhone(detail.data.fromNumber)} · {new Date(detail.data.startedAt).toLocaleString('fr-CA')} ·{' '}
              {detail.data.durationSeconds}s
            </span>
            <span className="judge__position tnum">
              {cursor + 1} / {items.length}
            </span>
          </header>

          {/* Listening comes first. A verdict formed from reading alone is not a verdict. */}
          {detail.data.recordingUrl && (
            <audio
              ref={audioRef}
              className="judge__audio"
              controls
              preload="none"
              src={detail.data.recordingUrl}
              key={detail.data.id}
            />
          )}

          <div className="judge__panes">
            <TranscriptPane
              title={t('transcripts.theirs')}
              words={diff.left}
              language={detail.data.theirs.language}
              hasNumber={detail.data.theirs.hasNumber}
              knownNumber={detail.data.knownNumber}
              t={t}
            />
            <TranscriptPane
              title={t('transcripts.ours')}
              subtitle={detail.data.ours.model ?? undefined}
              words={diff.right}
              language={detail.data.ours.language}
              confidence={detail.data.ours.confidence}
              hasNumber={detail.data.ours.hasNumber}
              knownNumber={detail.data.knownNumber}
              t={t}
            />
          </div>

          <footer className="judge__actions">
            {VERDICTS.map((verdict, index) => (
              <button
                key={verdict}
                type="button"
                className={`btn judge__verdict${current.verdict === verdict ? ' judge__verdict--on' : ''}`}
                disabled={judge.isPending}
                onClick={() => judge.mutate({ id: current.id, verdict })}
              >
                <kbd>{index + 1}</kbd>
                {t(`transcripts.verdict.${verdict}`)}
              </button>
            ))}
          </footer>

          {detail.data.reviews.length > 0 && (
            <p className="judge__reviews">
              {detail.data.reviews
                .map((r) => `${r.reviewer ?? '—'}: ${t(`transcripts.verdict.${r.verdict}`)}`)
                .join(' · ')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function TranscriptPane({
  title,
  subtitle,
  words,
  language,
  confidence,
  hasNumber,
  knownNumber,
  t,
}: {
  title: string;
  subtitle?: string;
  words: Array<{ text: string; same: boolean }>;
  language: string | null;
  confidence?: number | null;
  hasNumber: boolean | null;
  knownNumber: string | null;
  t: (key: string, vars?: Record<string, unknown>) => string;
}) {
  return (
    <section className="pane">
      <header className="pane__head">
        <h2>{title}</h2>
        {subtitle && <span className="pane__model">{subtitle}</span>}
        {language && (
          <span className="pane__lang">
            {language}
            {confidence != null && <span className="tnum"> {Math.round(confidence * 100)}%</span>}
          </span>
        )}
        {/*
          The one objective mark on the screen: the switch knows the number, so a transcript
          either recovered it or lost it. Shown per pane rather than as a summary because it is
          the fact a reviewer should weigh while the audio is still in their ear.
        */}
        {hasNumber !== null && knownNumber && (
          <span className={`pane__number${hasNumber ? ' pane__number--hit' : ' pane__number--miss'}`}>
            {hasNumber ? t('transcripts.numberFound') : t('transcripts.numberMissed')}
          </span>
        )}
      </header>

      <p className="pane__text">
        {words.length === 0 && <span className="pane__silent">{t('transcripts.nothing')}</span>}
        {words.map((word, index) => (
          <span key={index} className={word.same ? undefined : 'pane__differs'}>
            {word.text}{' '}
          </span>
        ))}
      </p>
    </section>
  );
}

/**
 * Word-level difference between two transcripts.
 *
 * A plain longest-common-subsequence, computed in the browser over a few hundred words. Marking
 * what differs is the entire value of putting them side by side — two paragraphs of similar
 * French are precisely what an eye slides over without noticing the name changed.
 *
 * Comparison ignores case, accents and punctuation, so `Hydro-Clean` and `hydroclean` do not
 * light up as a disagreement. The words shown are always the originals.
 */
function wordDiff(left: string, right: string): {
  left: Array<{ text: string; same: boolean }>;
  right: Array<{ text: string; same: boolean }>;
} {
  const a = left.split(/\s+/).filter(Boolean);
  const b = right.split(/\s+/).filter(Boolean);
  const key = (word: string) =>
    word
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

  const ka = a.map(key);
  const kb = b.map(key);

  // Capped: the table is O(n·m) and a pathological pair of transcripts should not freeze a tab.
  if (ka.length > 1200 || kb.length > 1200) {
    return { left: a.map((text) => ({ text, same: true })), right: b.map((text) => ({ text, same: true })) };
  }

  const lcs: number[][] = Array.from({ length: ka.length + 1 }, () => new Array(kb.length + 1).fill(0));
  for (let i = ka.length - 1; i >= 0; i -= 1) {
    for (let j = kb.length - 1; j >= 0; j -= 1) {
      lcs[i]![j] = ka[i] === kb[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }

  const leftOut: Array<{ text: string; same: boolean }> = [];
  const rightOut: Array<{ text: string; same: boolean }> = [];
  let i = 0;
  let j = 0;
  while (i < ka.length && j < kb.length) {
    if (ka[i] === kb[j]) {
      leftOut.push({ text: a[i]!, same: true });
      rightOut.push({ text: b[j]!, same: true });
      i += 1;
      j += 1;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      leftOut.push({ text: a[i]!, same: false });
      i += 1;
    } else {
      rightOut.push({ text: b[j]!, same: false });
      j += 1;
    }
  }
  while (i < ka.length) leftOut.push({ text: a[i++]!, same: false });
  while (j < kb.length) rightOut.push({ text: b[j++]!, same: false });

  return { left: leftOut, right: rightOut };
}
